import { doc, setDoc, getDoc, onSnapshot } from "firebase/firestore"
import { getAuth } from "firebase/auth"
import { db } from "lib/firebase"

/**
 * Firestore persistence middleware for Redux
 * This middleware automatically syncs Redux state changes to Firestore
 * and provides real-time synchronization across devices/sessions
 */

// Helper function to get the current user ID
const getCurrentUserId = () => {
  const auth = getAuth()
  return auth.currentUser?.uid || null
}

// Define which slices should be persisted to Firestore
const PERSISTABLE_SLICES = [
  "budget",
  "userPreferences",
  "chores",
  "expenses",
  "categories",
]

// Define collections mapping for different slices
const SLICE_COLLECTIONS = {
  budget: "userBudgets",
  userPreferences: "userPreferences",
  chores: "userChores",
  expenses: "userExpenses",
  categories: "userCategories",
}

// Global variables to track listeners
let globalUnsubscribeFunctions = []

/**
 * Creates Firestore persistence middleware
 * @param {Object} options Configuration options
 * @returns {Function} Redux middleware function
 */
export const createFirestorePersistenceMiddleware = (options = {}) => {
  const {
    debounceTime = 1000, // Debounce writes to prevent excessive Firestore calls
    enableRealTimeSync = true, // Enable real-time synchronization
    onError = (() => {}), // Error handler
    transformState = (state) => state, // State transformer before persisting
  } = options

  let debounceTimers = {}

  return (storeAPI) => {
    // Set up real-time listeners for each persistable slice
    if (enableRealTimeSync) {
      setupRealtimeListeners(storeAPI)
    }

    return (next) => (action) => {
      const result = next(action)

      // Check if this action affects a persistable slice
      const affectedSlice = getAffectedSlice(action)

      if (affectedSlice && PERSISTABLE_SLICES.includes(affectedSlice)) {
        const userId = getCurrentUserId()

        if (userId) {
          // Debounce the Firestore write to prevent excessive calls
          clearTimeout(debounceTimers[affectedSlice])
          debounceTimers[affectedSlice] = setTimeout(() => {
            persistSliceToFirestore(
              storeAPI,
              affectedSlice,
              userId,
              transformState,
              onError
            )
          }, debounceTime)
        }
      }

      return result
    }
  }
}

/**
 * Determines which slice is affected by an action
 * @param {Object} action Redux action
 * @returns {string|null} Slice name or null
 */
const getAffectedSlice = (action) => {
  if (!action.type) return null

  // Extract slice name from action type (e.g., "budget/setBudgetAmount" -> "budget")
  const parts = action.type.split("/")
  return parts.length > 1 ? parts[0] : null
}

/**
 * Persists a specific slice to Firestore
 * @param {Object} storeAPI Redux store API
 * @param {string} sliceName Name of the slice to persist
 * @param {string} userId Current user ID
 * @param {Function} transformState State transformation function
 * @param {Function} onError Error handler
 */
const persistSliceToFirestore = async (
  storeAPI,
  sliceName,
  userId,
  transformState,
  onError
) => {
  try {
    const state = storeAPI.getState()
    const sliceState = state[sliceName]

    if (!sliceState) return

    const transformedState = transformState(sliceState)
    const collectionName = SLICE_COLLECTIONS[sliceName]

    // Create user-specific document path
    const docRef = doc(db, collectionName, userId)

    // Add metadata
    const dataToStore = {
      ...transformedState,
      lastUpdated: new Date().toISOString(),
      version: 1,
    }

    await setDoc(docRef, dataToStore, { merge: true })

  } catch (error) {
    onError(`Error persisting ${sliceName} to Firestore:`, error)
  }
}

/**
 * Sets up real-time listeners for all persistable slices
 * @param {Object} storeAPI Redux store API
 */
const setupRealtimeListeners = (storeAPI) => {
  const userId = getCurrentUserId()

  if (!userId) {
    // Set up auth state listener to initialize listeners when user logs in
    const auth = getAuth()
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setupSliceListeners(storeAPI, user.uid)
      } else {
        // Clean up listeners when user logs out
        cleanupListeners()
      }
    })

    globalUnsubscribeFunctions.push(unsubscribeAuth)
  } else {
    setupSliceListeners(storeAPI, userId)
  }
}

/**
 * Sets up listeners for individual slices
 * @param {Object} storeAPI Redux store API
 * @param {string} userId User ID
 */
const setupSliceListeners = (storeAPI, userId) => {
  PERSISTABLE_SLICES.forEach((sliceName) => {
    const collectionName = SLICE_COLLECTIONS[sliceName]
    const docRef = doc(db, collectionName, userId)

    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data()

          // Remove metadata before dispatching to Redux
          const { lastUpdated, version, ...sliceData } = data

          // Dispatch action to update Redux state
          storeAPI.dispatch({
            type: `${sliceName}/hydrateFromFirestore`,
            payload: sliceData,
          })
        }
      },
      (error) => {
        // Handle permission errors gracefully
        if (error.code === "permission-denied") {
        } else {
        }
      }
    )

    globalUnsubscribeFunctions.push(unsubscribe)
  })
}

/**
 * Loads initial state from Firestore for a specific user
 * @param {string} userId User ID
 * @returns {Object} Initial state object
 */
export const loadInitialStateFromFirestore = async (userId) => {
  if (!userId) return {}

  const initialState = {}

  try {
    // Load state for each persistable slice
    const loadPromises = PERSISTABLE_SLICES.map(async (sliceName) => {
      const collectionName = SLICE_COLLECTIONS[sliceName]
      const docRef = doc(db, collectionName, userId)
      const docSnapshot = await getDoc(docRef)

      if (docSnapshot.exists()) {
        const data = docSnapshot.data()
        const { lastUpdated, version, ...sliceData } = data
        initialState[sliceName] = sliceData
      }
    })

    await Promise.all(loadPromises)

    return initialState
  } catch (error) {
    return {}
  }
}

/**
 * Cleans up all Firestore listeners
 */
const cleanupListeners = () => {
  globalUnsubscribeFunctions.forEach((unsubscribe) => {
    if (typeof unsubscribe === "function") {
      unsubscribe()
    }
  })
  globalUnsubscribeFunctions = []
}

/**
 * Higher-order reducer that adds Firestore hydration capability
 * @param {Function} reducer Original reducer
 * @returns {Function} Enhanced reducer
 */
export const withFirestoreHydration = (reducer) => {
  return (state, action) => {
    // Handle hydration from Firestore
    if (action.type.endsWith("/hydrateFromFirestore")) {
      return {
        ...state,
        ...action.payload,
        _hydrated: true,
      }
    }

    return reducer(state, action)
  }
}

// Export cleanup function for app shutdown
export const cleanupFirestorePersistence = cleanupListeners
